import { useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  BarChart,
  Bar,
  LineChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type {
  LlmDailyCost,
  LlmChannelBreakdown,
  LlmDailyModelCost,
  LlmTopLead,
} from "@school-sales-ai/shared";
import { modelLabel } from "@school-sales-ai/shared";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { useUsageMetrics, type DatePreset } from "@/hooks/useUsageMetrics";
import { cn } from "@/lib/utils";

const PRESET_LABELS: Record<DatePreset, string> = {
  "7d": "7 días",
  "30d": "30 días",
  "90d": "90 días",
  custom: "Personalizado",
};

const CHANNEL_COLORS: Record<string, string> = {
  web: "#dc2626",
  whatsapp: "#16a34a",
};

const PIE_COLORS = ["#dc2626", "#16a34a", "#2563eb", "#d97706"];

// Assigned by position in the day's series, so a model that only appears after a
// switch still gets a stable colour for the rest of the range.
const MODEL_COLORS = ["#2563eb", "#16a34a", "#d97706", "#7c3aed", "#dc2626"];

const TOKEN_COLORS = {
  inputTokens: "#3b82f6",
  outputTokens: "#8b5cf6",
  cacheWriteTokens: "#f59e0b",
  cacheReadTokens: "#10b981",
};

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-ink-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-400">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-ink-700">{title}</p>
      {children}
    </div>
  );
}

export function UsageDashboard(): React.JSX.Element {
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  const {
    data,
    isLoading,
    isError,
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    channel,
    setChannel,
  } = useUsageMetrics();

  const presets: DatePreset[] = ["7d", "30d", "90d", "custom"];

  const dailyWithMA = useMemo((): Array<LlmDailyCost & { movingAvg7d: number }> => {
    if (!data?.dailyCosts) return [];
    return data.dailyCosts.map((d: LlmDailyCost, i: number, arr: LlmDailyCost[]) => {
      const windowStart = Math.max(0, i - 6);
      const slice = arr.slice(windowStart, i + 1);
      const ma = slice.reduce((s: number, x: LlmDailyCost) => s + x.totalCostUsd, 0) / slice.length;
      return { ...d, movingAvg7d: parseFloat(ma.toFixed(6)) };
    });
  }, [data?.dailyCosts]);

  const cacheRateByDay = useMemo((): Array<{ date: string; cacheHitRate: number }> => {
    if (!data?.dailyCosts) return [];
    return data.dailyCosts.map((d: LlmDailyCost) => {
      const denom = d.inputTokens + d.cacheReadTokens;
      const rate = denom > 0 ? (d.cacheReadTokens / denom) * 100 : 0;
      return { date: d.date, cacheHitRate: parseFloat(rate.toFixed(2)) };
    });
  }, [data?.dailyCosts]);

  // Pivot (day, model) rows into one row per day with a column per model, which is
  // the shape a stacked chart needs. Days where a model did not run get 0 rather
  // than a gap, so the switch reads as one band replacing another.
  const { modelSeries, modelIds } = useMemo(() => {
    const rows: LlmDailyModelCost[] = data?.dailyModelCosts ?? [];
    const ids = [...new Set(rows.map((r) => r.model))].sort();
    const byDate = new Map<string, Record<string, number | string>>();
    for (const r of rows) {
      let row = byDate.get(r.date);
      if (!row) {
        row = { date: r.date };
        for (const id of ids) row[id] = 0;
        byDate.set(r.date, row);
      }
      row[r.model] = r.totalCostUsd;
    }
    return {
      modelSeries: [...byDate.values()].sort((a, b) =>
        String(a.date).localeCompare(String(b.date)),
      ),
      modelIds: ids,
    };
  }, [data?.dailyModelCosts]);

  if (!activeClientId) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-ink-400">
        Selecciona un cliente en el panel izquierdo para ver el dashboard de uso.
      </div>
    );
  }

  const kpis = data?.kpis;
  const topLeads = data?.topLeads ?? [];
  const channelBreakdown = data?.channelBreakdown ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {/* Page header */}
      <div className="border-b border-ink-100 bg-white px-6 py-4">
        <h1 className="text-lg font-bold text-ink-900">Dashboard de Uso LLM</h1>
        <p className="text-sm text-ink-400">Costos y tokens por conversación</p>
      </div>

      <div className="flex flex-col gap-6 p-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-lg border border-ink-200 bg-white p-1">
            {presets.map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  preset === p
                    ? "bg-lime text-moss"
                    : "text-ink-500 hover:bg-ink-50 hover:text-ink-900",
                )}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>

          {preset === "custom" && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-md border border-ink-200 px-3 py-1.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-lime-dk"
              />
              <span className="text-xs text-ink-400">→</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-md border border-ink-200 px-3 py-1.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-lime-dk"
              />
            </>
          )}

          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-lime-dk"
          >
            <option value="">Todos los canales</option>
            <option value="web">Web</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="instagram">Instagram</option>
          </select>
        </div>

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Error al cargar los datos de uso.
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl bg-ink-100" />
            ))}
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <KpiCard
                label="Costo total"
                value={`$${(kpis?.totalCostUsd ?? 0).toFixed(4)}`}
                sub="USD"
              />
              <KpiCard
                label="Costo promedio / conv."
                value={`$${(kpis?.avgCostPerConversation ?? 0).toFixed(5)}`}
                sub="USD"
              />
              <KpiCard
                label="Conversaciones"
                value={String(kpis?.totalConversations ?? 0)}
                sub="Total IA"
              />
              <KpiCard
                label="Cache hit rate"
                value={`${(kpis?.cacheHitRate ?? 0).toFixed(1)}%`}
                sub="Tokens desde caché"
              />
            </div>

            {/* Charts 2×2 grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Daily cost + 7d moving average */}
              <ChartCard title="Costo diario + media móvil 7d">
                <ResponsiveContainer width="100%" height={256}>
                  <ComposedChart data={dailyWithMA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${v.toFixed(3)}`} />
                    <Tooltip formatter={(v: number) => [`$${v.toFixed(5)}`, ""]} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="totalCostUsd"
                      name="Costo USD"
                      fill="#fca5a5"
                      stroke="#dc2626"
                      fillOpacity={0.3}
                    />
                    <Line
                      type="monotone"
                      dataKey="movingAvg7d"
                      name="Media 7d"
                      stroke="#7c3aed"
                      dot={false}
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Token stacked bar */}
              <ChartCard title="Tokens por día">
                <ResponsiveContainer width="100%" height={256}>
                  <BarChart data={data?.dailyCosts ?? []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="inputTokens" name="Input" stackId="tokens" fill={TOKEN_COLORS.inputTokens} />
                    <Bar dataKey="outputTokens" name="Output" stackId="tokens" fill={TOKEN_COLORS.outputTokens} />
                    <Bar dataKey="cacheWriteTokens" name="Cache write" stackId="tokens" fill={TOKEN_COLORS.cacheWriteTokens} />
                    <Bar dataKey="cacheReadTokens" name="Cache read" stackId="tokens" fill={TOKEN_COLORS.cacheReadTokens} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Cache hit rate over time */}
              <ChartCard title="Cache hit rate diario (%)">
                <ResponsiveContainer width="100%" height={256}>
                  <LineChart data={cacheRateByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v}%`} />
                    <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, "Cache hit rate"]} />
                    <Line
                      type="monotone"
                      dataKey="cacheHitRate"
                      name="Cache hit %"
                      stroke="#10b981"
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Daily cost split by model — the view a model change is readable in.
                  A pie over a range that spans the switch would average it away. */}
              <ChartCard title="Gasto diario por modelo">
                {modelIds.length === 0 ? (
                  <div className="flex h-64 items-center justify-center text-sm text-ink-400">
                    Sin datos
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={256}>
                      <BarChart data={modelSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v: number, name: string) => [
                            `$${v.toFixed(5)}`,
                            modelLabel(name),
                          ]}
                        />
                        <Legend formatter={(name: string) => modelLabel(name)} />
                        {modelIds.map((id, i) => (
                          <Bar
                            key={id}
                            dataKey={id}
                            stackId="model"
                            fill={MODEL_COLORS[i % MODEL_COLORS.length]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="mt-2 text-xs text-ink-400">
                      Incluye todo lo que factura contra este cliente, no solo las
                      conversaciones: la extracción pasiva y los resúmenes de lead corren
                      en Haiku y escriben en la misma tabla.
                    </p>
                  </>
                )}
              </ChartCard>

              {/* Channel cost split */}
              <ChartCard title="Costo por canal">
                {channelBreakdown.length === 0 ? (
                  <div className="flex h-64 items-center justify-center text-sm text-ink-400">
                    Sin datos
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={256}>
                    <PieChart>
                      <Pie
                        data={channelBreakdown}
                        dataKey="totalCostUsd"
                        nameKey="channel"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={({ channel: ch, totalCostUsd }: { channel: string; totalCostUsd: number }) =>
                          `${ch}: $${totalCostUsd.toFixed(4)}`
                        }
                      >
                        {channelBreakdown.map((entry: LlmChannelBreakdown, index: number) => (
                          <Cell
                            key={entry.channel}
                            fill={CHANNEL_COLORS[entry.channel] ?? PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => [`$${v.toFixed(5)}`, "Costo"]} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* Top leads table */}
            {topLeads.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm">
                <div className="border-b border-ink-100 px-5 py-3">
                  <p className="text-sm font-semibold text-ink-700">Top prospectos por costo</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-ink-100 text-left">
                        <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-ink-400">Ref</th>
                        <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-ink-400">Nombre</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-ink-400">Turnos</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-ink-400">Input</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-ink-400">Output</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-ink-400">Cache read</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-ink-400">Costo USD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-50">
                      {topLeads.map((lead: LlmTopLead) => (
                        <tr key={lead.leadId ?? lead.sessionRef} className="hover:bg-ink-50">
                          <td className="px-4 py-2.5 font-mono text-xs text-ink-500">{lead.sessionRef ?? "—"}</td>
                          <td className="px-4 py-2.5 text-ink-700">{lead.leadName ?? "—"}</td>
                          <td className="px-4 py-2.5 text-right text-ink-600">{lead.turnsCount}</td>
                          <td className="px-4 py-2.5 text-right text-ink-600">{lead.totalInputTokens.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right text-ink-600">{lead.totalOutputTokens.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right text-ink-600">{lead.totalCacheReadTokens.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right font-medium text-ink-900">${lead.totalCostUsd.toFixed(5)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
